import type { FactionId, PlayerProgress, Subject, TowerRun } from './types';
import { getCurrentProfileId } from './profile';
import { computeSubjectLevel as computeLevelForXp } from '../learn/scoring/xp';
import { computeCommanderLevel } from '../game/progression/commander';
import { DEFAULT_WEAPON_ID } from '../game/weapons/registry';
import { STARTER_MAIN_WEAPONS } from '../game/weapons/weapon-loot';

export const CURRENT_VERSION = 29;

/**
 * R-005 P3 — 預設解鎖的「主武器」id 清單 = **只有起手那幾把**。
 *
 * P2 曾為試玩把所有 registry 主武器全解鎖;P3(spec §4 取得)改回真正的設計:
 * 只給起手(STARTER_MAIN_WEAPONS = rapid-cannon + railgun),其餘 4 把靠
 *   - 打魔王首殺(weapon-loot.BOSS_MAIN_WEAPON)
 *   - 戰場武器箱(單場換裝)
 *   - 戰後寶箱(永久解鎖未擁有的)
 * 取得。預設武器 rapid-cannon 視為「永遠已解鎖」(resolveWeaponId 已硬保)。
 *
 * 鐵則:這只是「可使用該主武器的資格」(玩遊戲軌廣度),不碰 factionXP/skillLevels。
 */
export function defaultUnlockedMainWeapons(): string[] {
  return [...STARTER_MAIN_WEAPONS];
}

/**
 * Default 主人翁 / 英雄身份 for fresh + migrated saves.
 * 'celestial' (星算門) is the starter門派, already default-unlocked, so the
 * fallback never lands a kid in a locked hero. See docs/spec/HERO_SYSTEM_V1.md.
 */
export const DEFAULT_HERO_ID: FactionId = 'celestial';

/**
 * C02 Battle Pass — default season length (days). Phase 1 ships monthly seasons.
 * Tunable per-progress via `seasonPass.seasonLengthDays` (e.g. tests use 7).
 */
export const DEFAULT_SEASON_LENGTH_DAYS = 30;

/**
 * C01 Streak milestone tiers (登入連續天數).
 *
 * Distinct from `STREAK_REWARDS` which is the legacy v12 freeXP/cosmetic
 * grant table. These tiers drive the *celebration* path (mascot cheer +
 * banner highlight) at the canonical Duolingo 7/30/100 anchors mandated
 * by the C01 spec (`docs/GAME_TECHNIQUES_WIKI.md`).
 *
 * Why a separate list: the C01 wiki explicitly calls out 7/30/100 as the
 * tier breakpoints (freeXP / 限定 boss / mascot 慶祝). The legacy v12
 * STREAK_REWARDS goes 1/3/7/14/30 and is wired into the v12 economy —
 * touching it would regress `pendingStreakRewards` / `claimStreakRewards`
 * callers (parent dashboard, my-progress-card). Separation keeps both
 * compatible.
 */
export const STREAK_MILESTONES: readonly number[] = [7, 30, 100];

/** Hours before the streak is considered "at risk of breaking". 20h gives
 *  the kid a 4-hour buffer before the 24h midnight-UTC reset. The spec
 *  ("快斷了!" push prompt) — Phase 1 surfaces this via the home banner
 *  copy; Phase 2 wires it into push notifications. */
export const STREAK_AT_RISK_HOURS = 20;

// ── Streak reward thresholds ──────────────────────────────────────────────────
export interface StreakReward {
  id: string;
  days: number;
  freeXP: number;
  /** Cosmetic tank colour unlocked, if any. */
  cosmetic?: string;
}

export const STREAK_REWARDS: StreakReward[] = [
  { id: 'streak-1',  days: 1,  freeXP: 5 },
  { id: 'streak-3',  days: 3,  freeXP: 20 },
  { id: 'streak-7',  days: 7,  freeXP: 50,  cosmetic: 'gold' },
  { id: 'streak-14', days: 14, freeXP: 100, cosmetic: 'platinum' },
  { id: 'streak-30', days: 30, freeXP: 500, cosmetic: 'rainbow' },
];
const LEGACY_STORAGE_KEY = 'eternal-fortress.progress';
const profileStorageKey = (profileId: string) => `eternal-fortress.progress.${profileId}`;

// ── Cloud mirror hook (Supabase 雲端同步,見 src/shared/account.ts) ────────────
//
// `saveProgress` 寫完 localStorage 後,fire-and-forget 把同一份 progress
// 鏡射到雲端(若已登入 + 已設定雲端)。預設 undefined → 純本機,行為跟以前
// 100% 一樣(向後相容底線)。account.ts 在登入時註冊、登出時取消。
//
// `_mirrorSuppressed` 旗標讓「雲端拉資料寫回 localStorage」時能繞過 mirror,
// 否則 pull→saveProgress→mirror→push 會無限回送迴圈。cloud-sync.ts 的
// pullCloudIntoLocal 不走 saveProgress(直接寫 localStorage),但此 helper
// 仍提供給任何「需繞過 mirror 的本機批次寫入」場景使用。
type ProgressMirror = (profileId: string, p: PlayerProgress) => void;
let _mirror: ProgressMirror | undefined;
let _mirrorSuppressed = false;

/**
 * 註冊雲端鏡射回呼。account.ts 在登入時呼 `setProgressMirror(id => pushProfileToCloud(id))`,
 * 登出時呼 `setProgressMirror(undefined)` 取消。傳 undefined 即解除。
 */
export function setProgressMirror(fn: ProgressMirror | undefined): void {
  _mirror = fn;
}

/**
 * 在 `run` 期間暫停 mirror(避免回送迴圈)。同步函式,run 結束(含丟例外)後
 * 一定還原旗標(支援巢狀)。
 */
export function withMirrorSuppressed<T>(run: () => T): T {
  const prev = _mirrorSuppressed;
  _mirrorSuppressed = true;
  try {
    return run();
  } finally {
    _mirrorSuppressed = prev;
  }
}

export function createFreshProgress(studentName: string, profileId?: string): PlayerProgress {
  const now = new Date().toISOString();
  const resolvedProfileId = profileId ?? getCurrentProfileId();
  return {
    version: CURRENT_VERSION,
    studentName,
    // v28 主人翁系統 — 新玩家預設星算門 (default-unlocked starter).
    selectedHeroId: DEFAULT_HERO_ID,
    totalXP: 0,
    factionXP: { celestial: 0, runic: 0, techno: 0, elemental: 0, dominion: 0 },
    freeXP: 0,
    textbookPrefs: { math: '康軒', chinese: '康軒', english: '翰林', nature: '南一', social: '翰林' },
    unlockedSkills: [],
    currentLoadout: [],
    studentKPs: [],
    dailyXP: {},
    streak: { days: 0, lastLoginISO: '', longest: 0 },
    audio: { bgmVolume: 0.7, sfxVolume: 0.8 },
    tutorialDone: false,
    skillLevels: {},
    mistakes: [],
    campaignState: {
      chapter: 1,
      currentNodeId: null,
      visitedNodes: [],
      graphSeed: Date.now(),
    },
    towerHistory: [],
    bossesDefeated: [],
    streakRewardClaimed: [],
    dailyChallenge: null,
    activeProfileId: resolvedProfileId,
    parentSettings: {
      dailyTimeLimitMinutes: 0,
      lastPlayDate: '',
      minutesPlayedToday: 0,
      parentMode: false,
      // v24: 教學專業模式預設 ON (boss 2026-05-22 directive — 教學要專業).
      proMode: true,
    },
    unlockedTowerCosmetics: [],
    firstLevelUpDone: false,
    dailyHook: { firstLaunchAt: 0, claimedDays: [], tutorialBossCompleted: false },
    badges: { earned: [] },
    dailyQCount: {},
    seasonPass: {
      currentSeason: 1,
      seasonStartedAt: 0,
      xpThisSeason: 0,
      claimedTiers: [],
      longestSeasonXp: 0,
      seasonLengthDays: DEFAULT_SEASON_LENGTH_DAYS,
    },
    monsters: { caught: {} },
    // D-001 Phase A — 段位 + 學習積分 default zero state (Lv1, 0 XP per subject).
    subjectXP: { math: 0, chinese: 0, english: 0, nature: 0, social: 0 },
    subjectLevel: { math: 1, chinese: 1, english: 1, nature: 1, social: 1 },
    xpHistory: [],
    // D-001 Phase B — Adaptive Difficulty default: 每學科從 Lv1 (d1 暖身) 起步,
    // streak 都 0。Silent 推薦,無 UI 提示 (Prodigy 哲學)。
    adaptiveDifficultyState: {
      perSubject: {
        math:    { currentLevel: 1, streakCorrect: 0, streakWrong: 0, lastUpdated: '' },
        chinese: { currentLevel: 1, streakCorrect: 0, streakWrong: 0, lastUpdated: '' },
        english: { currentLevel: 1, streakCorrect: 0, streakWrong: 0, lastUpdated: '' },
        nature:  { currentLevel: 1, streakCorrect: 0, streakWrong: 0, lastUpdated: '' },
        social:  { currentLevel: 1, streakCorrect: 0, streakWrong: 0, lastUpdated: '' },
      },
    },
    // D-001 Phase C — push queue + daily cap default empty state.
    pendingPushEvents: [],
    dailyXpCap: { date: '', xpToday: 0, levelsToday: 0 },
    // v29 玩遊戲軌(指揮官等級)— 純帳號廣度,絕不影響核心戰力。
    // 新玩家:Lv1 / 0 XP,英雄只含預設 starter(其餘 Lv2~Lv5 解鎖),
    // 關卡 1..5,模式/造型空,QoL 全 0。
    commanderXp: 0,
    commanderLevel: 1,
    unlockedHeroes: [DEFAULT_HERO_ID],
    unlockedStages: [1, 2, 3, 4, 5],
    unlockedModes: [],
    unlockedAccessories: [],
    // R-005 P2:預設解鎖所有 registry 主武器(讓 boss 現在就能選/試玩;P3 後改靠掉落)。
    // 動態讀 registry,P4 加的主武器也自動含。boss-modifier 武器(weapon-*)仍靠打魔王解鎖。
    unlockedWeapons: defaultUnlockedMainWeapons(),
    // R-005 主武器系統:預設裝「連發機砲」(永遠可用),各武器等級空(視為 Lv1),素材 0。
    selectedWeapon: DEFAULT_WEAPON_ID,
    weaponLevels: {},
    weaponShards: 0,
    battleCampaign: { clearedStages: [], stageStars: {} },
    unlockedCommanderCosmetics: [],
    qol: { startingCoins: 0, rerollCharges: 0 },
    // 永久 meta 商店(PowerUp / meta-shop,LONG_TERM_PROGRESSION §7)— 玩遊戲軌。
    // 新玩家:0 金幣、空樹(底盤未抬)。與學習軌欄位物理分離(見 types.ts 註解)。
    metaCoins: 0,
    metaShop: {},
    createdAt: now,
    lastSavedAt: now,
  };
}

function todayKey(nowISO: string = new Date().toISOString()): string {
  return nowISO.slice(0, 10); // YYYY-MM-DD
}

function accrueDailyXP(p: PlayerProgress, amount: number): void {
  const key = todayKey();
  p.dailyXP[key] = (p.dailyXP[key] ?? 0) + amount;
}

function migrate(parsed: unknown): PlayerProgress | null {
  if (typeof parsed !== 'object' || parsed === null) return null;
  const p = parsed as Record<string, unknown> & { version?: number };
  // Chain migrations: each step upgrades one version, then falls through
  if (p.version === 1) {
    p.currentLoadout = p.currentLoadout ?? [];
    p.studentKPs = [];
    p.version = 2;
  }
  if (p.version === 2) {
    p.studentKPs = p.studentKPs ?? [];
    p.dailyXP = {};
    p.streak = { days: 0, lastLoginISO: '' };
    p.version = 3;
  }
  if (p.version === 3) {
    p.dailyXP = p.dailyXP ?? {};
    // Defensive: if streak is missing OR not an object (e.g., legacy test data with streak=0),
    // reset to default shape so tickStreakOnLoad doesn't crash.
    if (!p.streak || typeof p.streak !== 'object') {
      p.streak = { days: 0, lastLoginISO: '' };
    }
    p.version = 4;
  }
  if (p.version === 4) {
    p.audio = { bgmVolume: 0.7, sfxVolume: 0.8 };
    p.version = 5;
  }
  if (p.version === 5) {
    p.tutorialDone = p.tutorialDone ?? false;
    p.version = 6;
  }
  if (p.version === 6) {
    const skillLevels: Record<string, number> = {};
    for (const id of (p.skillsUnlocked as string[] | undefined) ?? (p.unlockedSkills as string[] | undefined) ?? []) {
      skillLevels[id] = 1;
    }
    p.skillLevels = skillLevels;
    p.version = 7;
  }
  if (p.version === 7) {
    p.mistakes = [];
    p.version = 8;
  }
  if (p.version === 8) {
    p.campaignState = {
      chapter: 1,
      currentNodeId: null,
      visitedNodes: [],
      graphSeed: Date.now(),
    };
    p.version = 9;
  }
  if (p.version === 9) {
    // familyCode and lastSyncedAt are optional — no default needed, just bump version
    p.version = 10;
  }
  if (p.version === 10) {
    p.towerHistory = [];
    p.version = 11;
  }
  if (p.version === 11) {
    // Backfill missing textbook publisher defaults for the 3 new subjects
    const prefs = (p.textbookPrefs ?? {}) as Record<string, string>;
    if (!prefs.english) prefs.english = '翰林';
    if (!prefs.nature)  prefs.nature  = '南一';
    if (!prefs.social)  prefs.social  = '翰林';
    p.textbookPrefs = prefs;
    // Add W4-GAL fields
    p.bossesDefeated = [];
    p.streakRewardClaimed = [];
    p.dailyChallenge = null;
    p.version = 12;
  }
  if (p.version === 12) {
    // W10-A: add activeProfileId — defaults to 'default' (legacy single profile)
    p.activeProfileId = p.activeProfileId ?? 'default';
    p.version = 13;
  }
  if (p.version === 13) {
    p.parentSettings = p.parentSettings ?? {
      dailyTimeLimitMinutes: 0,
      lastPlayDate: '',
      minutesPlayedToday: 0,
    };
    p.version = 14;
  }
  if (p.version === 14) {
    // v15 adds parentSettings.parentPin (4-digit PIN) and parentSettings.parentMode
    // (academy view-mode flag). Both are optional — undefined ⇒ unset/false. The PIN
    // stays undefined until the parent sets one (gates 家長管控 + 隱私管理 mutations).
    const ps = (p.parentSettings ?? {
      dailyTimeLimitMinutes: 0,
      lastPlayDate: '',
      minutesPlayedToday: 0,
    }) as Record<string, unknown>;
    if (ps.parentMode === undefined) ps.parentMode = false;
    p.parentSettings = ps as unknown as typeof p.parentSettings;
    p.version = 15;
  }
  if (p.version === 15) {
    // v16 adds:
    //   - PlayerProgress.unlockedTowerCosmetics (string[], default [])
    //   - TowerRun.deathCount / retiredAt — left undefined on legacy runs (both
    //     fields are optional on the type; backfill with 0/'manual' lazily as
    //     callers need them).  Pre-existing run history is preserved as-is.
    if (!Array.isArray(p.unlockedTowerCosmetics)) {
      p.unlockedTowerCosmetics = [];
    }
    p.version = 16;
  }
  if (p.version === 16) {
    // v17 adds firstLevelUpDone — drives the Pokémon-evolution overlay on first level-up.
    if (p.firstLevelUpDone === undefined) p.firstLevelUpDone = false;
    p.version = 17;
  }
  if (p.version === 17) {
    // v18 adds dailyHook — C05 Day-1 hook state (gift + 7-day calendar + tutorial boss).
    // Legacy players who already have a save (no firstLaunchAt) get firstLaunchAt = 0
    // here; `ensureDailyHook` (called on first home render) will seed it lazily so the
    // 7-day calendar treats existing players as fresh-start on next login.
    const dh = p.dailyHook as { firstLaunchAt?: number; claimedDays?: number[]; tutorialBossCompleted?: boolean } | undefined;
    p.dailyHook = {
      firstLaunchAt: dh?.firstLaunchAt ?? 0,
      claimedDays: Array.isArray(dh?.claimedDays) ? dh!.claimedDays! : [],
      tutorialBossCompleted: dh?.tutorialBossCompleted === true,
    };
    p.version = 18;
  }
  if (p.version === 18) {
    // v19 adds `streak.longest` — C01 historical-best tracking for the
    // loss-aversion banner. Backfill: if the legacy save has a real running
    // streak, seed longest = days (kid's current run IS their best so far).
    // If streak missing/malformed (e.g. legacy v1-v3 progress with streak=0
    // number that escaped earlier defensive repair), reset cleanly.
    const s = p.streak as unknown;
    if (!s || typeof s !== 'object') {
      p.streak = { days: 0, lastLoginISO: '', longest: 0 } as unknown as typeof p.streak;
    } else {
      const sObj = s as { days?: number; lastLoginISO?: string; longest?: number };
      const days = typeof sObj.days === 'number' ? sObj.days : 0;
      const lastLoginISO = typeof sObj.lastLoginISO === 'string' ? sObj.lastLoginISO : '';
      const longest = typeof sObj.longest === 'number' ? sObj.longest : days;
      p.streak = { days, lastLoginISO, longest } as unknown as typeof p.streak;
    }
    p.version = 19;
  }
  if (p.version === 19) {
    // v20 adds `badges` — C04 Badge + 解鎖儀式感 state. Fresh players start
    // with an empty earned list and no pending reveal. Defensive: if a
    // hand-crafted fixture already carries a partial badges shape we keep
    // the earned array but normalise the structure (avoid undefined.push
    // crashes in `unlockBadge`).
    const b = p.badges as { earned?: string[]; lastUnlocked?: { id?: string; at?: number } } | undefined;
    const earned = Array.isArray(b?.earned) ? b!.earned!.filter((x): x is string => typeof x === 'string') : [];
    const lu = b?.lastUnlocked;
    const normalizedLastUnlocked =
      lu && typeof lu.id === 'string' && typeof lu.at === 'number'
        ? { id: lu.id, at: lu.at }
        : undefined;
    p.badges = normalizedLastUnlocked
      ? { earned, lastUnlocked: normalizedLastUnlocked }
      : { earned };
    p.version = 20;
  }
  if (p.version === 20) {
    // v21 adds `dailyQCount` (Record<YYYY-MM-DD, number>) — per-day question
    // count for the C06 parent dashboard 7d/30d summary. Legacy saves get
    // an empty map; `recordQuestionAnswered` populates it lazily from the
    // next lesson onward. We DO NOT backfill historical days because the
    // info simply isn't recoverable from existing data (studentKPs.totalAttempts
    // is cumulative, not per-day).
    //
    // Note: v21 is the unified bump that resolves the C04+C06 parallel-merge
    // conflict — C04 had landed v20 (badges) and C06 also targeted v20
    // (dailyQCount); promoting C06's payload to v21 keeps the chain linear
    // and the migrations idempotent across either feature-branch ancestry.
    if (typeof p.dailyQCount !== 'object' || p.dailyQCount === null || Array.isArray(p.dailyQCount)) {
      p.dailyQCount = {};
    }
    p.version = 21;
  }
  if (p.version === 21) {
    // v22 adds `seasonPass` — C02 Battle Pass 季節軌道 state. Legacy saves
    // start a fresh Season 1 with `seasonStartedAt = 0` (unseeded); the home
    // page calls `tickSeason` on first render which lazy-seeds the timer so
    // existing players don't get robbed of day-1 of S1. We DO NOT backfill
    // `xpThisSeason` from any existing metric — seasonXP is a new sidechannel
    // that only accumulates from C02-aware emit points (streak milestone /
    // badge unlock / day-one claim) added in this commit.
    //
    // Defensive: a handcrafted fixture may pre-stage a partial seasonPass
    // shape — preserve numeric fields when valid, fall back to defaults
    // otherwise. The `seasonLengthDays` default uses the constant so a
    // future bump (e.g. weekly seasons) auto-applies to legacy saves.
    const sp = p.seasonPass as Partial<{
      currentSeason: number;
      seasonStartedAt: number;
      xpThisSeason: number;
      claimedTiers: number[];
      longestSeasonXp: number;
      seasonLengthDays: number;
    }> | undefined;
    p.seasonPass = {
      currentSeason: typeof sp?.currentSeason === 'number' && sp.currentSeason >= 1 ? sp.currentSeason : 1,
      seasonStartedAt: typeof sp?.seasonStartedAt === 'number' ? sp.seasonStartedAt : 0,
      xpThisSeason: typeof sp?.xpThisSeason === 'number' && sp.xpThisSeason >= 0 ? sp.xpThisSeason : 0,
      claimedTiers: Array.isArray(sp?.claimedTiers)
        ? sp.claimedTiers.filter((x): x is number => typeof x === 'number')
        : [],
      longestSeasonXp: typeof sp?.longestSeasonXp === 'number' && sp.longestSeasonXp >= 0 ? sp.longestSeasonXp : 0,
      seasonLengthDays: typeof sp?.seasonLengthDays === 'number' && sp.seasonLengthDays >= 1
        ? sp.seasonLengthDays
        : DEFAULT_SEASON_LENGTH_DAYS,
    };
    p.version = 22;
  }
  if (p.version === 22) {
    // v23 adds `monsters` — C03 怪獸收集 + 進化 state. Legacy saves start
    // with an empty caught map and no pending reveal. Defensive: if a
    // hand-crafted fixture pre-stages a partial monsters shape we keep
    // the non-numeric junk filtered out of `caught` (avoid NaN crashes
    // in `caughtCount` arithmetic) and normalise `lastNew`.
    //
    // We DO NOT backfill any monsters from existing fields (badges /
    // bossesDefeated etc.). C03 is a NEW retention sidechannel — the
    // first catch fires when the kid hits a v23-aware emit point
    // (lesson-complete / boss-defeated / streak-milestone / badge-unlocked)
    // after this migration. This keeps the celebration honest.
    const m = p.monsters as { caught?: Record<string, unknown>; lastNew?: { id?: string; at?: number } } | undefined;
    const rawCaught = (m && typeof m.caught === 'object' && m.caught !== null && !Array.isArray(m.caught)) ? m.caught : {};
    const cleanCaught: Record<string, number> = {};
    for (const [id, val] of Object.entries(rawCaught)) {
      if (typeof id !== 'string' || id.length === 0) continue;
      if (typeof val === 'number' && Number.isFinite(val) && val > 0) {
        cleanCaught[id] = Math.floor(val);
      }
    }
    const ln = m?.lastNew;
    const normalisedLastNew =
      ln && typeof ln.id === 'string' && typeof ln.at === 'number'
        ? { id: ln.id, at: ln.at }
        : undefined;
    p.monsters = normalisedLastNew
      ? { caught: cleanCaught, lastNew: normalisedLastNew }
      : { caught: cleanCaught };
    p.version = 23;
  }
  if (p.version === 23) {
    // v24 adds `parentSettings.proMode` — 教學專業模式 (default true).
    //
    // Boss directive (2026-05-22): 教學介面應該要正式一點,教學介面不需要遊戲
    // 化、不需要有音樂。教學是要專業的. Legacy saves get proMode = true so the
    // very next answer-screen render is the polished professional UI; parents
    // who want the legacy gamified experience can opt out in settings.
    //
    // Defensive: parentSettings may be missing entirely (paranoid legacy
    // fixtures) — create the minimum shape so the proMode bit can land.
    const ps = (p.parentSettings ?? {
      dailyTimeLimitMinutes: 0,
      lastPlayDate: '',
      minutesPlayedToday: 0,
    }) as Record<string, unknown>;
    if (ps.proMode === undefined) ps.proMode = true;
    p.parentSettings = ps as unknown as typeof p.parentSettings;
    p.version = 24;
  }
  if (p.version === 24) {
    // v25 adds D-001 Phase A — 段位 + 學習積分 (subjectXP / subjectLevel / xpHistory).
    //
    // Why default to zero (not derived from factionXP, even though D-001 §5.2
    // suggested it): risk R-001 says subjectXP <-> factionXP double-write drift
    // is the top risk. Phase A bossfix is to keep the two ledgers fully
    // independent — subjectXP is brand-new for the learn-entry app and starts
    // at 0; faction-XP keeps doing its game-side job untouched. (Phase D
    // backlog may revisit a unification refactor.)
    //
    // Defensive: hand-crafted fixtures may pre-stage partial subjectXP /
    // subjectLevel maps. We normalise them rather than overwriting, so test
    // setups that seed (e.g.) `subjectXP.math = 200` survive migration with
    // the level re-derived to keep XP and Lv consistent.
    const subjects: Subject[] = ['math', 'chinese', 'english', 'nature', 'social'];
    const existingXP = (p.subjectXP && typeof p.subjectXP === 'object' && !Array.isArray(p.subjectXP))
      ? p.subjectXP as Partial<Record<Subject, number>>
      : {};
    const seededXP: Partial<Record<Subject, number>> = {};
    for (const s of subjects) {
      const v = existingXP[s];
      seededXP[s] = typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;
    }
    p.subjectXP = seededXP;

    const existingLv = (p.subjectLevel && typeof p.subjectLevel === 'object' && !Array.isArray(p.subjectLevel))
      ? p.subjectLevel as Partial<Record<Subject, number>>
      : {};
    const seededLv: Partial<Record<Subject, number>> = {};
    for (const s of subjects) {
      const xp = seededXP[s] ?? 0;
      const derivedFromXp = computeLevelForXp(xp);
      const v = existingLv[s];
      const lv = typeof v === 'number' && Number.isFinite(v) && v >= 1 ? Math.floor(v) : derivedFromXp;
      // Never let stored level fall below what the XP would unlock.
      seededLv[s] = Math.max(lv, derivedFromXp);
    }
    p.subjectLevel = seededLv;

    p.xpHistory = Array.isArray(p.xpHistory) ? p.xpHistory : [];
    p.version = 25;
  }
  if (p.version === 25) {
    // v26 adds D-001 Phase B — Adaptive Difficulty Controller (Prodigy-style
    // silent). 每學科起始 currentLevel=1 (從暖身起步), streak 都 0.
    //
    // Why per-subject (not per-KP): D-001 §4 spec 明定 adaptive 單位 = 學科,
    // 避免每換一個 KP 都從頭起算難度,孩子在同一學科內保持連貫推薦。
    //
    // Defensive: pre-staged adaptiveDifficultyState (hand-crafted test fixtures)
    // 保留;只補齊缺漏 subject 的 default。
    const subjects: Subject[] = ['math', 'chinese', 'english', 'nature', 'social'];
    const existing = (p.adaptiveDifficultyState
      && typeof p.adaptiveDifficultyState === 'object'
      && !Array.isArray(p.adaptiveDifficultyState))
      ? p.adaptiveDifficultyState as { perSubject?: Record<string, unknown> }
      : {};
    const existingPerSubject = (existing.perSubject && typeof existing.perSubject === 'object')
      ? existing.perSubject as Record<string, {
          currentLevel?: unknown;
          streakCorrect?: unknown;
          streakWrong?: unknown;
          lastUpdated?: unknown;
        }>
      : {};
    const seededPerSubject: Partial<Record<Subject, {
      currentLevel: 1 | 2 | 3;
      streakCorrect: number;
      streakWrong: number;
      lastUpdated: string;
    }>> = {};
    for (const s of subjects) {
      const stale = existingPerSubject[s];
      const rawLv = stale?.currentLevel;
      const lv: 1 | 2 | 3 = (rawLv === 1 || rawLv === 2 || rawLv === 3) ? rawLv : 1;
      const sc = typeof stale?.streakCorrect === 'number' && stale.streakCorrect >= 0
        ? Math.floor(stale.streakCorrect) : 0;
      const sw = typeof stale?.streakWrong === 'number' && stale.streakWrong >= 0
        ? Math.floor(stale.streakWrong) : 0;
      const lu = typeof stale?.lastUpdated === 'string' ? stale.lastUpdated : '';
      seededPerSubject[s] = {
        currentLevel: lv,
        streakCorrect: sc,
        streakWrong: sw,
        lastUpdated: lu,
      };
    }
    p.adaptiveDifficultyState = { perSubject: seededPerSubject };
    p.version = 26;
  }
  if (p.version === 26) {
    // v27 adds D-001 Phase C — pendingPushEvents queue + dailyXpCap tracker.
    //
    // pendingPushEvents: side-channel queue for the LINE Bot worker (D-005).
    //   schema-only; this Phase doesn't push anything. Defensive: pre-staged
    //   queue entries are kept as-is (test fixtures may seed them); type/level
    //   validation happens at enqueue time, not migration time.
    //
    // dailyXpCap: per-day cumulative XP + level-up counters. The boss prompt
    //   says "同日 totalXP > 500 → 截斷" so we track ACROSS subjects (a single
    //   counter per day, not per-subject). Reset to empty-day shape; today's
    //   accrual is left to the first applyDailyCap() call after migration.
    //
    // Why not pre-derive today's counts from xpHistory: xpHistory may contain
    //   today's entries from before the cap landed (pre-Phase-C plays). To
    //   keep the migration deterministic and avoid double-counting we start
    //   the counters at zero — kids get a one-time grace window the first
    //   day Phase C lands. Acceptable trade-off.
    p.pendingPushEvents = Array.isArray(p.pendingPushEvents) ? p.pendingPushEvents : [];
    const existingCap = (p.dailyXpCap && typeof p.dailyXpCap === 'object' && !Array.isArray(p.dailyXpCap))
      ? p.dailyXpCap as { date?: unknown; xpToday?: unknown; levelsToday?: unknown }
      : {};
    p.dailyXpCap = {
      date: typeof existingCap.date === 'string' ? existingCap.date : '',
      xpToday: typeof existingCap.xpToday === 'number' && existingCap.xpToday >= 0
        ? Math.floor(existingCap.xpToday) : 0,
      levelsToday: typeof existingCap.levelsToday === 'number' && existingCap.levelsToday >= 0
        ? Math.floor(existingCap.levelsToday) : 0,
    };
    p.version = 27;
  }
  if (p.version === 27) {
    // v28 adds `selectedHeroId` — 主人翁 / 英雄身份 (玩家所選門派).
    //
    // Legacy saves had no hero concept; the battle scene used the stage faction
    // (hubFactionId) as the gameplay source. v28 splits the two: hero玩法/戰車
    // reads selectedHeroId, stage背景/boss keeps reading hubFactionId. Backfill
    // every existing save with the default starter 'celestial' (already
    // default-unlocked) so nobody migrates into a locked / undefined hero.
    //
    // Defensive: a hand-crafted fixture may already carry a valid FactionId —
    // preserve it; otherwise fall back to the default.
    const VALID: readonly FactionId[] =
      ['celestial', 'runic', 'techno', 'elemental', 'dominion'];
    const existing = p.selectedHeroId as unknown;
    p.selectedHeroId = (typeof existing === 'string' && VALID.includes(existing as FactionId))
      ? (existing as FactionId)
      : DEFAULT_HERO_ID;
    p.version = 28;
  }
  if (p.version === 28) {
    // v29 玩遊戲軌(指揮官等級)。全部 optional,給安全預設值。
    // spec docs/spec/PROGRESSION_AND_ACCOUNTS.md §3.3.
    //
    // commanderXp/Level:legacy 一律從 0 / Lv1 起算(**不**從 factionXP 或
    // freeXP 推導 — 兩軌物理獨立,避免把舊學習成就灌成玩遊戲等級,違反
    // 雙軌鐵則 §7 紅線)。
    if (typeof p.commanderXp !== 'number' || !Number.isFinite(p.commanderXp)) p.commanderXp = 0;
    p.commanderLevel = computeCommanderLevel(p.commanderXp as number);
    // 已解鎖英雄:沿用既有 selectedHeroId 為「已擁有」,至少含 default starter。
    const heroes = new Set<FactionId>(['celestial']);
    const sel = p.selectedHeroId as FactionId | undefined;
    if (sel) heroes.add(sel);
    p.unlockedHeroes = Array.isArray(p.unlockedHeroes) ? p.unlockedHeroes : [...heroes];
    p.unlockedStages = Array.isArray(p.unlockedStages) ? p.unlockedStages : [1, 2, 3, 4, 5];
    p.unlockedModes = Array.isArray(p.unlockedModes) ? p.unlockedModes : [];
    p.unlockedAccessories = Array.isArray(p.unlockedAccessories) ? p.unlockedAccessories : [];
    p.unlockedWeapons = Array.isArray(p.unlockedWeapons) ? p.unlockedWeapons : [];
    p.battleCampaign = (p.battleCampaign && typeof p.battleCampaign === 'object'
      && Array.isArray((p.battleCampaign as { clearedStages?: unknown }).clearedStages))
      ? p.battleCampaign
      : { clearedStages: [], stageStars: {} };
    p.unlockedCommanderCosmetics = Array.isArray(p.unlockedCommanderCosmetics)
      ? p.unlockedCommanderCosmetics : [];
    p.qol = (p.qol && typeof p.qol === 'object' && !Array.isArray(p.qol))
      ? p.qol : { startingCoins: 0, rerollCharges: 0 };
    p.version = 29;
  }
  if (p.version === 29) {
    // unlockedWeapons 是 v29 之後新增的 optional 欄位(打贏魔王解鎖武器類型,玩遊戲軌
    // 廣度,不 bump version,比照 unlockedAccessories)。既有 v29 存檔缺此欄 → 補空陣列。
    if (!Array.isArray(p.unlockedWeapons)) p.unlockedWeapons = [];
    // R-005 P3:只確保「起手主武器」在 unlockedWeapons(union,不倒退既有解鎖)。
    //   - 既有存檔已解鎖的(boss-modifier weapon-* / P2 試玩時拿到的主武器)全保留。
    //   - 但**不再**force-union 全部 registry 主武器 —— 其餘 4 把改靠打魔王 / 武器箱 /
    //     戰後寶箱取得(weapon-loot)。新存檔(createFreshProgress)亦只給起手。
    {
      const existing = p.unlockedWeapons as string[];
      const have = new Set(existing);
      for (const id of defaultUnlockedMainWeapons()) {
        if (!have.has(id)) { existing.push(id); have.add(id); }
      }
    }
    // R-005 主武器系統:selectedWeapon / weaponLevels 是 v29 之後新增 optional 欄位
    // (不 bump version,同 unlockedWeapons)。既有存檔缺 → 補預設(預設裝連發機砲、等級空)。
    if (typeof p.selectedWeapon !== 'string' || !p.selectedWeapon) p.selectedWeapon = DEFAULT_WEAPON_ID;
    if (!p.weaponLevels || typeof p.weaponLevels !== 'object' || Array.isArray(p.weaponLevels)) {
      p.weaponLevels = {};
    }
    // R-005 P2:weaponShards 武器升級素材(玩遊戲軌)。既有存檔缺/髒值 → 補 0。
    if (typeof p.weaponShards !== 'number' || !Number.isFinite(p.weaponShards) || p.weaponShards < 0) {
      p.weaponShards = 0;
    }
    return p as unknown as PlayerProgress;
  }
  return null;
}

// ── v28 主人翁 / 英雄身份 helpers ──────────────────────────────────────────────
//
// selectedHeroId is the玩家所選門派 — drives the in-battle active-skill 玩法 +
// 戰車外觀 + 立繪. 學習與遊戲分離鐵則: pure-game identity, 選英雄 NEVER pops a
// question. Always read through `getSelectedHeroId` so legacy / undefined saves
// fall back to the default starter rather than crashing downstream lookups.

const VALID_HERO_IDS: readonly FactionId[] =
  ['celestial', 'runic', 'techno', 'elemental', 'dominion'];

/** Currently-selected hero門派, falling back to the default starter
 *  ('celestial') when unset/invalid (legacy saves, corrupt fixtures). */
export function getSelectedHeroId(p: PlayerProgress): FactionId {
  const id = p.selectedHeroId;
  return (typeof id === 'string' && VALID_HERO_IDS.includes(id)) ? id : DEFAULT_HERO_ID;
}

/** Set the selected hero門派. Caller is responsible for `saveProgress(p)`
 *  afterwards so the write can be batched. Invalid ids are ignored (no-op)
 *  to keep the field from drifting to garbage. */
export function setSelectedHeroId(p: PlayerProgress, id: FactionId): void {
  if (VALID_HERO_IDS.includes(id)) {
    p.selectedHeroId = id;
  }
}

/**
 * Migrate legacy `eternal-fortress.progress` to per-profile key.
 * This is a one-time, idempotent operation.
 */
function migrateLegacyKey(): void {
  const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (!legacyRaw) return;
  // Only migrate if the per-profile key doesn't already exist
  const defaultKey = profileStorageKey('default');
  if (!localStorage.getItem(defaultKey)) {
    localStorage.setItem(defaultKey, legacyRaw);
  }
  localStorage.removeItem(LEGACY_STORAGE_KEY);
}

export function loadProgress(profileId?: string): PlayerProgress | null {
  // One-time legacy migration: move old key to .default
  migrateLegacyKey();

  const id = profileId ?? getCurrentProfileId();
  const raw = localStorage.getItem(profileStorageKey(id));
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw);
    return migrate(parsed);
  } catch {
    return null;
  }
}

export function saveProgress(p: PlayerProgress, profileId?: string): void {
  p.lastSavedAt = new Date().toISOString();
  const id = profileId ?? p.activeProfileId ?? getCurrentProfileId();
  localStorage.setItem(profileStorageKey(id), JSON.stringify(p));
  // 雲端鏡射(fire-and-forget,登出/未設定雲端時 _mirror 為 undefined → no-op)。
  // suppress 期間(雲端拉資料寫回本機)跳過,避免回送迴圈。錯誤由 mirror 自己吞。
  if (_mirror && !_mirrorSuppressed) {
    _mirror(id, p);
  }
}

export function addFactionXP(p: PlayerProgress, faction: FactionId, amount: number): void {
  p.factionXP[faction] += amount;
  p.totalXP += amount;
  accrueDailyXP(p, amount);
}

export function addFreeXP(p: PlayerProgress, amount: number): void {
  p.freeXP += amount;
  accrueDailyXP(p, amount);
}

export function allocateFreeXP(p: PlayerProgress, faction: FactionId, amount: number): void {
  if (amount > p.freeXP) {
    throw new Error(`allocateFreeXP: insufficient freeXP (have ${p.freeXP}, need ${amount})`);
  }
  p.freeXP -= amount;
  addFactionXP(p, faction, amount);
}

/**
 * C06 — bump today's question-count bucket by `n` (default 1). Wired into
 * the generic lesson runner + exam runner so every scored answer accrues.
 * Safe to call on legacy progress (creates the map lazily). Callers are
 * still responsible for `saveProgress(p)` so the write can be batched.
 */
export function recordQuestionAnswered(p: PlayerProgress, n: number = 1, nowISO: string = new Date().toISOString()): void {
  if (n <= 0) return;
  if (!p.dailyQCount || typeof p.dailyQCount !== 'object') p.dailyQCount = {};
  const key = nowISO.slice(0, 10);
  p.dailyQCount[key] = (p.dailyQCount[key] ?? 0) + n;
}

/** Prepend a TowerRun to towerHistory and trim to 50. */
export function recordTowerRun(p: PlayerProgress, run: TowerRun): void {
  p.towerHistory = [run, ...(p.towerHistory ?? [])].slice(0, 50);
}

/** Idempotently unlock a tower cosmetic by id (no duplicates). Added in v16. */
export function unlockTowerCosmetic(p: PlayerProgress, cosmeticId: string): void {
  p.unlockedTowerCosmetics ??= [];
  if (!p.unlockedTowerCosmetics.includes(cosmeticId)) {
    p.unlockedTowerCosmetics.push(cosmeticId);
  }
}

/** True iff the given tower cosmetic id has been unlocked. Added in v16. */
export function hasUnlockedCosmetic(p: PlayerProgress, cosmeticId: string): boolean {
  return (p.unlockedTowerCosmetics ?? []).includes(cosmeticId);
}

export function skillUnlocked(p: PlayerProgress, skillId: string): boolean {
  return p.unlockedSkills.includes(skillId);
}

/**
 * Returns streak rewards that are now eligible but not yet claimed.
 * Call after tickStreakOnLoad so streak.days is current.
 */
export function pendingStreakRewards(p: PlayerProgress): StreakReward[] {
  const claimed = p.streakRewardClaimed ?? [];
  return STREAK_REWARDS.filter(
    (r) => p.streak.days >= r.days && !claimed.includes(r.id),
  );
}

/**
 * Claim all pending streak rewards: add freeXP, unlock cosmetics,
 * mark as claimed. Returns the list of claimed rewards (may be empty).
 */
export function claimStreakRewards(p: PlayerProgress): StreakReward[] {
  const pending = pendingStreakRewards(p);
  if (pending.length === 0) return [];
  // Ensure arrays exist (defensive for old saves that were migrated)
  p.streakRewardClaimed ??= [];
  for (const reward of pending) {
    addFreeXP(p, reward.freeXP);
    if (reward.cosmetic) {
      const cosmeticId = `cosmetic:tank-${reward.cosmetic}`;
      if (!p.unlockedSkills.includes(cosmeticId)) {
        p.unlockedSkills.push(cosmeticId);
      }
    }
    p.streakRewardClaimed.push(reward.id);
  }
  return pending;
}

/** Record that a boss was defeated (idempotent — no duplicate IDs). */
export function recordBossDefeated(p: PlayerProgress, bossId: string): void {
  p.bossesDefeated ??= [];
  if (!p.bossesDefeated.includes(bossId)) {
    p.bossesDefeated.push(bossId);
  }
}

/**
 * Record that a weapon type was unlocked (打贏魔王首殺解鎖,玩遊戲軌「廣度」)。
 * 冪等(無重複 id);回傳 true = 這次是「新」解鎖(供結算顯示「解鎖新武器」橫幅,
 * 重打同魔王不再彈)。
 *
 * 鐵則(學習/遊戲分離):只記「可使用該武器的資格」(unlockedWeapons),**絕不**
 * 寫 factionXP/freeXP/skillLevels/永久傷害 — 武器戰鬥效果走單場 RunModifiers。
 */
export function recordWeaponUnlocked(p: PlayerProgress, weaponId: string): boolean {
  if (!weaponId) return false;
  p.unlockedWeapons ??= [];
  if (p.unlockedWeapons.includes(weaponId)) return false;
  p.unlockedWeapons.push(weaponId);
  return true;
}

// ── R-005 P2 主武器升級素材(weaponShards)+ 等級曲線 ──────────────────────────
//
// 鐵則(學習/遊戲分離,WEAPON_SYSTEM_SPEC §0 / §5):weaponShards 是純武器養成貨幣
// (玩遊戲軌),戰鬥勝利結算發放、升級時扣除。發放/扣除/升級**絕不**碰
// factionXP/freeXP/skillLevels/修煉值 — 武器威力 = 武器等級曲線,非戰力。

/** 主武器升級每級花費的素材(線性遞增:Lv1→2 花 1×、Lv2→3 花 2× …)。 */
export const WEAPON_UPGRADE_BASE_COST = 3;

/** 目前等級(weaponLevels[id]),缺省/髒值視為 Lv1。純讀,不寫。 */
export function getWeaponLevel(p: PlayerProgress, weaponId: string): number {
  const lv = p.weaponLevels?.[weaponId];
  return typeof lv === 'number' && Number.isFinite(lv) && lv >= 1 ? Math.floor(lv) : 1;
}

/** 從 `fromLevel` 升到下一級要花的素材(線性:cost = base × fromLevel)。 */
export function weaponUpgradeCost(fromLevel: number): number {
  const lv = Number.isFinite(fromLevel) && fromLevel >= 1 ? Math.floor(fromLevel) : 1;
  return WEAPON_UPGRADE_BASE_COST * lv;
}

/**
 * 發放武器升級素材(玩遊戲軌)。戰鬥勝利結算呼叫,比照 addCommanderXp 寫法。
 * 鐵則:只動 weaponShards,絕不碰戰力欄位。負數/0 → no-op。回傳新餘額。
 */
export function addWeaponShards(p: PlayerProgress, amount: number): number {
  if (!Number.isFinite(amount) || amount <= 0) return p.weaponShards ?? 0;
  p.weaponShards = (p.weaponShards ?? 0) + Math.floor(amount);
  return p.weaponShards;
}

/**
 * 升級一把主武器(花 weaponShards → weaponLevels[id]++)。
 * - 素材不足 → 不升級,回傳 { ok:false }(餘額/等級不變)。
 * - 成功 → 扣素材、等級 +1,回傳 { ok:true, level, shards }。
 * caller 負責 saveProgress(p)。
 *
 * 鐵則:只動 weaponShards / weaponLevels(武器養成獨立線),**絕不**碰
 * factionXP/freeXP/skillLevels/修煉值。
 */
export function upgradeMainWeapon(
  p: PlayerProgress,
  weaponId: string,
): { ok: boolean; level: number; shards: number; cost: number } {
  const level = getWeaponLevel(p, weaponId);
  const cost = weaponUpgradeCost(level);
  const have = p.weaponShards ?? 0;
  if (!weaponId || have < cost) {
    return { ok: false, level, shards: have, cost };
  }
  p.weaponShards = have - cost;
  p.weaponLevels = p.weaponLevels ?? {};
  p.weaponLevels[weaponId] = level + 1;
  return { ok: true, level: level + 1, shards: p.weaponShards, cost };
}

/**
 * Record N minutes of play time today.
 * Resets the counter at midnight (date change).
 * Returns whether the daily limit has been exceeded AFTER recording.
 * If limit is 0 (unlimited), always returns false.
 */
export function recordPlayMinutes(p: PlayerProgress, minutes: number, nowISO: string = new Date().toISOString()): boolean {
  if (!p.parentSettings) {
    p.parentSettings = { dailyTimeLimitMinutes: 0, lastPlayDate: '', minutesPlayedToday: 0 };
  }
  const today = nowISO.slice(0, 10);
  if (p.parentSettings.lastPlayDate !== today) {
    p.parentSettings.lastPlayDate = today;
    p.parentSettings.minutesPlayedToday = 0;
  }
  p.parentSettings.minutesPlayedToday += minutes;
  // Also append to the per-day history for the parent weekly report
  // (林媽媽 2026-05-21 fix #5 — 本週遊戲總時間).
  p.parentSettings.dailyPlayMinutes ??= {};
  p.parentSettings.dailyPlayMinutes[today] =
    (p.parentSettings.dailyPlayMinutes[today] ?? 0) + minutes;
  if (p.parentSettings.dailyTimeLimitMinutes === 0) return false;
  return p.parentSettings.minutesPlayedToday >= p.parentSettings.dailyTimeLimitMinutes;
}

// ── Parent PIN helpers (林媽媽 2026-05-21 fix #1 — PIN protection) ───────────
//
// PIN is hashed with SubtleCrypto SHA-256 and stored as 64-char lowercase hex
// in `parentSettings.parentPin`. The PIN itself never leaves localStorage.
//
// Legacy/test note: a raw 4-digit string is ALSO accepted by `hasParentPin`
// for backward-compat with v15 records that may have been written by the
// pre-hash code path (the field was named the same).

/** SHA-256 → lowercase hex. Pure helper, isomorphic between window + jsdom. */
async function sha256Hex(input: string): Promise<string> {
  // SubtleCrypto is available in browsers, Node ≥16 (globalThis.crypto.subtle),
  // and jsdom for tests.
  const bytes = new TextEncoder().encode(input);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  const arr = Array.from(new Uint8Array(digest));
  return arr.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** True iff a stored PIN is currently set (in either hashed or legacy form). */
export function hasParentPin(p: PlayerProgress): boolean {
  const stored = p.parentSettings?.parentPin;
  if (typeof stored !== 'string' || stored.length === 0) return false;
  // Hashed (64 hex) or legacy 4-digit are both considered "set".
  return /^[0-9a-f]{64}$/i.test(stored) || /^\d{4}$/.test(stored);
}

/**
 * Hash the given 4-digit PIN and persist it to `parentSettings.parentPin`.
 * Throws if `pin` is not exactly 4 decimal digits. Caller is responsible for
 * passing the result of `saveProgress(p)` afterwards (so the call can be
 * batched with other mutations).
 */
export async function setParentPin(p: PlayerProgress, pin: string): Promise<void> {
  if (!/^\d{4}$/.test(pin)) {
    throw new Error('setParentPin: pin must be exactly 4 decimal digits');
  }
  if (!p.parentSettings) {
    p.parentSettings = { dailyTimeLimitMinutes: 0, lastPlayDate: '', minutesPlayedToday: 0 };
  }
  p.parentSettings.parentPin = await sha256Hex(pin);
}

/**
 * Verify the given attempt against the stored PIN.
 * Returns false when no PIN is set or the attempt fails to match.
 * Supports legacy plain 4-digit storage by direct compare as well as hashed.
 */
export async function verifyParentPin(p: PlayerProgress, attempt: string): Promise<boolean> {
  const stored = p.parentSettings?.parentPin;
  if (typeof stored !== 'string' || stored.length === 0) return false;
  if (!/^\d{4}$/.test(attempt)) return false;
  // Hashed (64 hex) — compare hash of attempt
  if (/^[0-9a-f]{64}$/i.test(stored)) {
    const attemptHash = await sha256Hex(attempt);
    return attemptHash === stored.toLowerCase();
  }
  // Legacy plain 4-digit (predates the hash rollout) — direct compare.
  return stored === attempt;
}

/**
 * Record N minutes against today's bucket in `parentSettings.dailyPlayMinutes`.
 * (Separate from `recordPlayMinutes` which tracks the rolling daily-limit
 * counter — this one is the per-day history surfaced in the parent weekly
 * report.) Idempotent: pass minutes >0 and same nowISO to accumulate.
 */
export function recordDailyPlayMinutes(p: PlayerProgress, minutes: number, nowISO: string = new Date().toISOString()): void {
  if (!p.parentSettings) {
    p.parentSettings = { dailyTimeLimitMinutes: 0, lastPlayDate: '', minutesPlayedToday: 0 };
  }
  p.parentSettings.dailyPlayMinutes ??= {};
  const key = nowISO.slice(0, 10);
  p.parentSettings.dailyPlayMinutes[key] = (p.parentSettings.dailyPlayMinutes[key] ?? 0) + minutes;
}

/**
 * Sum of dailyPlayMinutes for the 7 days ending at `nowISO` (inclusive).
 * Returns 0 if no history present.
 */
export function weeklyPlayMinutes(p: PlayerProgress, nowISO: string = new Date().toISOString()): number {
  const map = p.parentSettings?.dailyPlayMinutes;
  if (!map) return 0;
  const today = nowISO.slice(0, 10);
  let total = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    total += map[d.toISOString().slice(0, 10)] ?? 0;
  }
  return total;
}

/**
 * Returns true if the daily game time limit has already been reached today.
 * If limit is 0 (unlimited), always returns false.
 */
export function isDailyLimitReached(p: PlayerProgress, nowISO: string = new Date().toISOString()): boolean {
  if (!p.parentSettings || p.parentSettings.dailyTimeLimitMinutes === 0) return false;
  const today = nowISO.slice(0, 10);
  if (p.parentSettings.lastPlayDate !== today) return false;
  return p.parentSettings.minutesPlayedToday >= p.parentSettings.dailyTimeLimitMinutes;
}

export function tickStreakOnLoad(p: PlayerProgress, nowISO: string = new Date().toISOString()): void {
  // Defensive: legacy progress where streak is a number (v1-v3) or undefined → repair in place.
  // Also covers the rare case where a v19 record was written without `longest`
  // (e.g. handcrafted test fixtures predating C01).
  if (!p.streak || typeof p.streak !== 'object') {
    (p as unknown as { streak: { days: number; lastLoginISO: string; longest: number } }).streak =
      { days: 0, lastLoginISO: '', longest: 0 };
  } else if (typeof p.streak.longest !== 'number') {
    p.streak.longest = p.streak.days;
  }
  const today = todayKey(nowISO);
  const last = p.streak.lastLoginISO ? todayKey(p.streak.lastLoginISO) : '';
  if (last === today) {
    // Already counted today — still ensure longest >= days as a safety net
    // (covers fixtures that set days > longest before the first tick).
    if (p.streak.days > p.streak.longest) p.streak.longest = p.streak.days;
    return;
  }
  if (last === '') {
    p.streak.days = 1;
  } else {
    // Check if last was yesterday
    const d = new Date(today); d.setUTCDate(d.getUTCDate() - 1);
    const yesterday = d.toISOString().slice(0, 10);
    p.streak.days = (last === yesterday) ? p.streak.days + 1 : 1;
  }
  // C01: track historical best. Updated only after `days` has been recomputed
  // so a fresh reset back to 1 doesn't clobber the previous record.
  if (p.streak.days > p.streak.longest) p.streak.longest = p.streak.days;
  p.streak.lastLoginISO = nowISO;
}

// ── C01 Streak helpers (loss aversion + milestone detection) ─────────────────

/**
 * Hours since the last login (or +Infinity if never logged in).
 * Pure helper for `isStreakAtRisk` / banner copy.
 */
export function hoursSinceLastLogin(p: PlayerProgress, nowMs: number = Date.now()): number {
  if (!p.streak?.lastLoginISO) return Infinity;
  const last = Date.parse(p.streak.lastLoginISO);
  if (!Number.isFinite(last)) return Infinity;
  return Math.max(0, (nowMs - last) / (60 * 60 * 1000));
}

/**
 * True when the kid has an active streak (>=1 day) AND it's been
 * >= STREAK_AT_RISK_HOURS (default 20h) since their last login. Drives
 * the "🔥 快斷了!" loss-aversion banner copy.
 *
 * Returns false when:
 *   - No streak yet (days < 1) — nothing to lose
 *   - Just logged in (< 20h) — no need to warn
 *   - Already broken (> 48h, days got reset on next tick anyway)
 *
 * We DON'T warn after 48h because by then the kid has likely already
 * "lost" the streak in their head — telling them "quick, save it!" is
 * misleading since the next tickStreakOnLoad call will reset to 1.
 */
export function isStreakAtRisk(p: PlayerProgress, nowMs: number = Date.now()): boolean {
  if (!p.streak || p.streak.days < 1) return false;
  const h = hoursSinceLastLogin(p, nowMs);
  return h >= STREAK_AT_RISK_HOURS && h < 48;
}

/**
 * Most recent C01 milestone (7 / 30 / 100) the kid has just *reached* on
 * THIS tick — i.e. `prevDays < tier <= newDays`. Returns null when no
 * milestone was crossed. Caller (mascot integration in home page) uses
 * this to fire the celebrate animation EXACTLY ONCE per crossing.
 *
 * Designed to be called RIGHT AFTER `tickStreakOnLoad` with `prevDays`
 * captured before the tick.
 */
export function streakMilestoneCrossed(
  prevDays: number,
  newDays: number,
): number | null {
  if (newDays <= prevDays) return null;
  for (let i = STREAK_MILESTONES.length - 1; i >= 0; i--) {
    const tier = STREAK_MILESTONES[i];
    if (prevDays < tier && newDays >= tier) return tier;
  }
  return null;
}
